/*
 * @Description: 定位设置数据 hook（G1 读取 / G2 保存）。
 *   弹窗打开（enabled）时拉取 config/settings/get，save 提交 config/settings/save。
 *   mock 模式读默认值、保存直接成功，不打真实接口。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import {
  fetchGroupSettings,
  saveGroupSettings
} from '../../../actions/locationCockpit'
import { DEFAULT_LOCATION_SETTINGS, isCockpitMockEnabled } from '../constants'
import { CockpitEnvelope } from '../types/cockpit.types'
import { LocationMode, LocationSettings } from '../types/settings.types'

/** 后端 bool 可能是 true/1/'true'，统一规整到 JS bool */
const toBool = (v: unknown): boolean => v === true || v === 1 || v === 'true'

const LOCATION_MODE_VALUES: LocationMode[] = ['walk', 'vehicle', 'mixed']
const normalizeMode = (v: unknown): LocationMode =>
  LOCATION_MODE_VALUES.includes(v as LocationMode)
    ? (v as LocationMode)
    : DEFAULT_LOCATION_SETTINGS.locationMode

/** 接口返回（可能字段缺失）→ 完整 LocationSettings（G1-2 六字段），缺啥补默认 */
function normalize(
  data: Partial<LocationSettings> | null | undefined,
  groupId: string
): LocationSettings {
  const d = data || {}
  return {
    groupId: d.groupId || groupId,
    workStartTime: d.workStartTime || DEFAULT_LOCATION_SETTINGS.workStartTime,
    workEndTime: d.workEndTime || DEFAULT_LOCATION_SETTINGS.workEndTime,
    workDays: d.workDays || DEFAULT_LOCATION_SETTINGS.workDays,
    pauseOnHoliday: toBool(d.pauseOnHoliday),
    locationMode: normalizeMode(d.locationMode),
    allowMemberViewOwnTrack: toBool(d.allowMemberViewOwnTrack)
  }
}

export interface UseLocationSettingsResult {
  settings: LocationSettings | null
  loading: boolean
  saving: boolean
  /** 提交保存，返回是否成功 */
  save: (next: LocationSettings) => Promise<boolean>
}

/**
 * 定位设置读取 / 保存。
 *
 * @param groupId - 当前团队长 id
 * @param enabled - 是否启用（弹窗打开时才拉取）
 */
export function useLocationSettings(
  groupId: string,
  enabled: boolean
): UseLocationSettingsResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [settings, setSettings] = useState<LocationSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    if (isMock) {
      setSettings(normalize(null, groupId))
      return
    }
    if (!groupId) return
    setLoading(true)
    dispatch(fetchGroupSettings({ groupId }))
      .then((res: CockpitEnvelope<Partial<LocationSettings>>) => {
        if (!aliveRef.current) return
        setLoading(false)
        // 1002/1004（无配置）data 为 null，normalize 回退默认值
        setSettings(
          normalize(res && res.code === 200 ? res.data : null, groupId)
        )
      })
      .catch(() => {
        if (!aliveRef.current) return
        setLoading(false)
        setSettings(normalize(null, groupId))
      })
  }, [dispatch, groupId, enabled, isMock])

  const save = useCallback(
    (next: LocationSettings): Promise<boolean> => {
      if (isMock) {
        setSettings(next)
        return Promise.resolve(true)
      }
      if (!groupId) return Promise.resolve(false)
      setSaving(true)
      return dispatch(saveGroupSettings({ ...next, groupId }))
        .then((res: CockpitEnvelope<Partial<LocationSettings>>) => {
          if (aliveRef.current) setSaving(false)
          const ok = !!res && res.code === 200
          if (ok && aliveRef.current) {
            setSettings(normalize(res.data ?? next, groupId))
          }
          return ok
        })
        .catch(() => {
          if (aliveRef.current) setSaving(false)
          return false
        })
    },
    [dispatch, groupId, isMock]
  )

  return { settings, loading, saving, save }
}
